import {
  SimpleForm,
  NumberInput,
  required,
  useRecordContext,
  maxValue,
  useCreate,
  useNotify,
  Loading,
  useGetList,
  Toolbar,
  SaveButton,
  useTranslate,
  useUpdate,
  minValue,
  BooleanInput,
} from 'react-admin'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Box,
  Alert,
  AlertTitle,
} from '@mui/material'
import { Calendar } from '../../components/BigCalendar'
import { BranchSelectInput } from '../../components/form'
import { BRANCHCONFIG_OVERRIDE_FIELDS } from '../../providers/localization/constant'
import { useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { TimeStepInput } from '../../components/domains/branch-config/TimeStepInput'
import moment from 'moment'
import 'moment/locale/th'
import './rbc-style.css'
import { branchConfigTimeConverter } from '../../utils/times'
import _ from 'lodash'
import { closeTime, openTime } from '../../utils/validators'

const OverrideConfigCalendar = (): JSX.Element => {
  const [open, setOpen] = useState(false)
  const notify = useNotify()
  const t = useTranslate()
  const record = useRecordContext()
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEvent, setShowEvent] = useState(false)
  const { setValue, getValues } = useFormContext()
  const [isClose, setIsClose] = useState(false)

  const { data, isLoading, error } = useGetList('branch_config_override', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'create_at', order: 'ASC' },
    filter: { branch_code: record?.branch_code },
    meta: { isPaginate: false },
  })

  const handleClickOpen = (): void => {
    setOpen(true)
  }

  const handleIsCloseChange = (event): void => {
    setIsClose(event.target.checked)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClose = (_event = null, reason = null): void => {
    if (reason && reason == 'backdropClick') return
    setSelectedEvent(null)
    setShowEvent(false)
    setOpen(false)
    setIsClose(false)
  }

  const onError = useCallback(
    (error): void => {
      const errorMessage = error.response.data.message
      return notify(errorMessage || 'ra.validation.create_branch_config_fail', { type: 'error' })
    },
    [notify],
  )

  const onSuccess = useCallback((): void => {
    notify(
      <Alert severity='success'>
        <AlertTitle>สำเร็จ!</AlertTitle>
        {t('ra.notification.create_branch_config_success')}
      </Alert>,
    )
    setOpen(false)
    setTimeout((): void => {
      window.open(`/branch_config/${record?.id}`, '_self')
    }, 800)
  }, [notify, record?.id, t])
  const [create] = useCreate(undefined, undefined, { onError, onSuccess })
  const [update] = useUpdate(undefined, undefined, { onError, onSuccess })

  if (isLoading) {
    return <Loading />
  }
  if (error) {
    return (
      <Alert severity='error'>
        <AlertTitle>เกิดข้อผิดพลาด!</AlertTitle>
        เกิดข้อผิดพลาดขณะโหลดข้อมูล ({error?.message})
      </Alert>
    )
  }
  const events = data.map((e) => {
    const openTimeStr = branchConfigTimeConverter(e.open_time)
    const closeTimeStr = branchConfigTimeConverter(e.close_time)
    const applyDate = moment(e.apply_date)
    const openTime = moment(openTimeStr, 'HH:mm')
    const closeTime = moment(closeTimeStr, 'HH:mm')
    const start = moment(applyDate).set({
      hour: openTime.get('hour'),
      minute: openTime.get('minute'),
      second: openTime.get('second'),
    })
    const end = moment(applyDate).set({
      hour: closeTime.get('hour'),
      minute: closeTime.get('minute'),
      second: closeTime.get('second'),
    })
    return {
      id: e.id,
      title: `${e.slot} | ${openTimeStr.format('HH:mm')} - ${closeTimeStr.format('HH:mm')}`,
      start: start.toDate(),
      end: end.toDate(),
      close: e.is_close,
      bgColor: e.is_close ? '#919EAB' : '#F38D33',
      // bgColor: '#F6BE00',
    }
  })
  const handleSubmit = (data): void => {
    const {
      id,
      type,
      branch_code: branchCode,
      open_slot: slot,
      open_time: openTime,
      close_time: closeTime,
    } = data
    const o = _.isNumber(openTime) ? branchConfigTimeConverter(openTime).format('HH:mm') : openTime
    const c = _.isNumber(closeTime)
      ? branchConfigTimeConverter(closeTime).format('HH:mm')
      : closeTime
    if (type === 'create') {
      create('branch_config_override', {
        data: {
          branch_code: branchCode,
          slot: slot,
          apply_date: getValues('apply_date'),
          open_time: o,
          close_time: c,
          is_close: isClose,
        },
      })
    } else {
      update('branch_config_override', {
        id: id,
        data: {
          branch_code: branchCode,
          slot: slot,
          apply_date: getValues('apply_date'),
          open_time: o,
          close_time: c,
          is_close: isClose,
        },
      })
    }
  }
  const Event = ({ event }): JSX.Element => {
    return (
      <>
        {event.close ? (
          <div style={{ color: '#212B36', fontSize: 15, fontWeight: 'bold', textAlign: 'center' }}>
            ปิดสาขา
          </div>
        ) : (
          <div style={{ color: '#F4F6F8', fontSize: 15, lineHeight: 1.4, fontWeight: 'bold' }}>
            {event.title.split('|')[0].trim()} ช่องจอด
            <br />
            เปิด {moment(event.start).format('HH:mm')}
            <br />
            ปิด {moment(event.end).format('HH:mm')}
            <br />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <Calendar
        events={events}
        views={['month']}
        step={15}
        components={{
          event: Event,
        }}
        onSelectEvent={(e): void => {
          if (moment(e.start).endOf('day') < moment()) {
            setShowEvent(true)
          }
          const ev = _.find(data, ['id', e.id])
          setSelectedEvent(ev)
          setValue('apply_date', moment(e.start).format('YYYY-MM-DD'))
          setIsClose(ev.is_close)
          handleClickOpen()
        }}
        onSelectSlot={(e): void => {
          const { start, end, slots } = e
          const eventsForThisDay = events.filter(
            (event) =>
              moment(event.start) >= moment(start).startOf('day') &&
              moment(event.start) < moment(end).endOf('day'),
          )
          if (slots.length > 1) {
            return notify(
              <Alert severity='warning'>
                <AlertTitle>เกิดข้อผิดพลาด!</AlertTitle>
                ไม่สามารถลากเพื่อเลือกจำนวนวันมากกว่า 1 วันได้
              </Alert>,
            )
          }
          if (moment(start) < moment().startOf('day')) {
            return notify(
              <Alert severity='warning'>
                <AlertTitle>เกิดข้อผิดพลาด!</AlertTitle>
                ไม่สามารถเพิ่มรายการวันก่อนหน้าได้
              </Alert>,
            )
          }
          if (eventsForThisDay.length > 0) {
            const eId = eventsForThisDay[0].id
            const ev = _.find(data, ['id', eId])
            setSelectedEvent(ev)
          }
          setValue('apply_date', moment(start).format('YYYY-MM-DD'))
          handleClickOpen()
        }}
      ></Calendar>
      <Dialog fullWidth={false} maxWidth='md' open={open} onClose={handleClose}>
        <Grid container>
          <Grid item xs={10}>
            <DialogTitle sx={{ typography: 'h4' }}>
              {!showEvent && (!selectedEvent ? t('ra.action.create') : t('ra.action.edit'))}
              การตั้งค่ารายวัน
            </DialogTitle>
          </Grid>
          <Grid item xs={2}>
            <DialogActions>
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </Grid>
        </Grid>
        <DialogContent sx={{ pt: 0 }}>
          <DialogContentText>
            กำหนดค่าช่องที่เปิดและเวลาเปิด-ปิดสาขา วันที่{' '}
            {moment(getValues('apply_date'), 'YYYY-MM-DD').format('LL')}
          </DialogContentText>
          <SimpleForm
            record={
              selectedEvent
                ? {
                    ...selectedEvent,
                    branch_code: record.branch_code,
                    slot: record.slot,
                    open_slot: selectedEvent.slot,
                    is_close: selectedEvent.is_close,
                    type: 'update',
                  }
                : { ...record, type: 'create' }
            }
            onSubmit={handleSubmit}
            toolbar={
              !showEvent && (
                <Toolbar>
                  <SaveButton sx={{ ml: 'auto' }} />
                </Toolbar>
              )
            }
            sx={{ pt: '20px' }}
          >
            <Box
              noValidate
              component='form'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                m: 'auto',
                width: 'fit-content',
              }}
            >
              <Grid container item spacing={1} xs={12}>
                <Grid item xs={12}>
                  <BooleanInput
                    color='primary'
                    source='is_close'
                    label={`${BRANCHCONFIG_OVERRIDE_FIELDS}.is_close`}
                    defaultValue={false}
                    onChange={handleIsCloseChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberInput
                    source='slot'
                    label={`${BRANCHCONFIG_OVERRIDE_FIELDS}.slot`}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberInput
                    source='open_slot'
                    label={`${BRANCHCONFIG_OVERRIDE_FIELDS}.open_slot`}
                    validate={[required(), minValue(1), maxValue(record?.slot)]}
                    disabled={showEvent || isClose}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TimeStepInput
                    source='open_time'
                    disabled={showEvent || isClose}
                    validate={[required(), openTime]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TimeStepInput
                    source='close_time'
                    disabled={showEvent || isClose}
                    validate={[required(), closeTime]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BranchSelectInput source='branch_code' disabled />
                </Grid>
              </Grid>
            </Box>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default OverrideConfigCalendar
